import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/contexts/LanguageContext';
import { getLocalizedRoute, type RouteKey } from '@/lib/routes';
import { seoMetaMap } from '@/lib/seoMeta';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  routeKey?: RouteKey;
  slug?: string;
  type?: 'website' | 'article' | 'service';
  structuredData?: object;
  breadcrumbs?: Array<{ name: string; url: string }>;
  author?: string;
  publishDate?: string;
  modifiedDate?: string;
  noIndex?: boolean;
}

export const SEOOptimizer = ({
  title,
  description,
  keywords,
  image,
  url,
  routeKey,
  slug,
  type = 'website',
  structuredData,
  breadcrumbs,
  author,
  publishDate,
  modifiedDate,
  noIndex = false,
}: SEOProps) => {
  const { language } = useLanguage();
  
  // Generate canonical and alternate URLs
  const baseUrl = import.meta.env.VITE_SITE_URL || 'https://pricezing-monetize-growth.lovable.app';
  const resolvedImage = image || `${baseUrl}/lovable-uploads/dee6dedc-7cf9-4ee6-8015-479fa0287b6c.png`;
  let canonicalUrl = url || baseUrl;
  let enUrl = baseUrl;
  let esUrl = `${baseUrl}/es`;
  
  if (routeKey) {
    const enPath = getLocalizedRoute(routeKey, 'en');
    const esPath = getLocalizedRoute(routeKey, 'es');
    enUrl = `${baseUrl}${enPath}${slug ? `/${slug}` : ''}`;
    esUrl = `${baseUrl}${esPath}${slug ? `/${slug}` : ''}`;
    canonicalUrl = language === 'es' ? esUrl : enUrl;
  }

  // Auto-generate SEO content based on page
  const generateSEOContent = () => {
    // Prefer centralized unique metadata when a known route is provided (except blog posts)
    if (routeKey && routeKey !== 'blogPost') {
      const meta = seoMetaMap[routeKey as Exclude<RouteKey, 'blogPost'>];
      if (meta) {
        return {
          en: {
            title: meta.en.title,
            description: meta.en.description,
            keywords: meta.en.keywords,
          },
          es: {
            title: meta.es.title,
            description: meta.es.description,
            keywords: meta.es.keywords,
          }
        };
      }
    }

    // Fallback to props-based values with sensible defaults
    const defaultContent = {
      en: {
        title: 'PriceZing | Pricing Strategy Consulting & AI Platform',
        description: 'Expert pricing strategy consulting, AI monitoring, and certified market research to grow margins across LATAM & U.S.',
        keywords: 'pricing strategy, AI pricing, price optimization, market research, LATAM pricing'
      },
      es: {
        title: 'PriceZing | Consultoría de Precios y Plataforma IA',
        description: 'Consultoría experta en precios, monitoreo con IA e investigación certificada para crecer márgenes en LATAM y EE.UU.',
        keywords: 'estrategia de precios, precios IA, optimización de precios, investigación de mercado, LATAM'
      }
    };

    return {
      en: {
        title: title || defaultContent.en.title,
        description: description || defaultContent.en.description,
        keywords: keywords || defaultContent.en.keywords
      },
      es: {
        title: title || defaultContent.es.title,
        description: description || defaultContent.es.description,
        keywords: keywords || defaultContent.es.keywords
      }
    };
  };

  const seoContent = generateSEOContent();
  const current = seoContent[language];

  // Generate structured data based on page type
  const generateStructuredData = () => {
    const organizationData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "PriceZing",
      "description": current.description,
      "url": baseUrl,
      "logo": `${baseUrl}/lovable-uploads/dee6dedc-7cf9-4ee6-8015-479fa0287b6c.png`,
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+1 (346) 705-7320",
        "contactType": "customer service",
        "availableLanguage": ["English", "Spanish"]
      },
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "16192 COASTAL HWY",
        "addressLocality": "Lewes",
        "addressRegion": "DE",
        "postalCode": "19958",
        "addressCountry": "US"
      },
      "sameAs": [
        "https://linkedin.com/company/pricezingsolutions",
        "https://twitter.com/pricezingsolutions"
      ]
    };

    let schemaData: any[] = [organizationData];

    // Add WebSite schema for home page
    if (canonicalUrl === baseUrl || canonicalUrl === `${baseUrl}/es`) {
      schemaData.push({
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "PriceZing",
        "url": baseUrl,
        "inLanguage": language === 'es' ? 'es' : 'en',
        "potentialAction": {
          "@type": "SearchAction",
          "target": `${baseUrl}/search?q={query}`,
          "query-input": "required name=query"
        }
      });
    }

    // Add Service schema for service pages
    if (type === 'service') {
      schemaData.push({
        "@context": "https://schema.org",
        "@type": "Service",
        "name": current.title,
        "provider": organizationData,
        "areaServed": ["United States", "Latin America"],
        "description": current.description,
        "serviceType": "Pricing Strategy"
      });
    }

    // Add Article schema for blog posts
    if (type === 'article' && publishDate) {
      schemaData.push({
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": current.title,
        "description": current.description,
        "inLanguage": language === 'es' ? 'es' : 'en',
        "author": {
          "@type": "Organization",
          "name": author || "PriceZing Team"
        },
        "datePublished": publishDate,
        "dateModified": modifiedDate || publishDate,
        "image": resolvedImage,
        "mainEntityOfPage": canonicalUrl,
        "publisher": organizationData
      });
    }

    // Add Breadcrumb schema if breadcrumbs provided
    if (breadcrumbs && breadcrumbs.length > 0) {
      schemaData.push({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((crumb, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": crumb.name,
          "item": crumb.url
        }))
      });
    }

    return structuredData || schemaData;
  };

  const finalStructuredData = generateStructuredData();

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={language} />
      <title>{current.title}</title>
      <meta name="description" content={current.description} />
      <meta name="keywords" content={current.keywords} />
      <meta name="author" content="PriceZing Solutions" />
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"} />
      <meta name="language" content={language} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Alternate Languages */}
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="es" href={esUrl} />
      <link rel="alternate" hrefLang="x-default" href={enUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={current.title} />
      <meta property="og:description" content={current.description} />
      <meta property="og:image" content={resolvedImage} />
      <meta property="og:site_name" content="PriceZing Solutions" />
      <meta property="og:locale" content={language === 'es' ? 'es_ES' : 'en_US'} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={current.title} />
      <meta name="twitter:description" content={current.description} />
      <meta name="twitter:image" content={resolvedImage} />
      <meta name="twitter:site" content="@pricezingsolutions" />
      <meta name="twitter:creator" content="@pricezingsolutions" />
      
      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#F07E2D" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Preconnect for Performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* Structured Data */}
      {Array.isArray(finalStructuredData) && finalStructuredData.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
      
      {/* Publication/Modified Dates for Articles */}
      {publishDate && <meta property="article:published_time" content={publishDate} />}
      {modifiedDate && <meta property="article:modified_time" content={modifiedDate} />}
      {author && <meta name="author" content={author} />}
    </Helmet>
  );
};